import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { PageHero } from "@/components/site/PageHero";
import { SiteFooter } from "@/components/site/SiteFooter";

export const Route = createFileRoute("/past-papers")({
  head: () => ({
    meta: [
      { title: "Past Papers — Grade 11 & Grade 12 (CAPS) | adaptED" },
      {
        name: "description",
        content:
          "Free CAPS-aligned past papers for Grade 11 and Grade 12. Download official DBE exam papers and turn every paper into personalised revision with adaptED.",
      },
      {
        name: "keywords",
        content:
          "past papers, grade 11 past papers, grade 12 past papers, matric past papers, CAPS past papers, NSC past papers, South African exam papers, matric revision",
      },
      { name: "robots", content: "index, follow" },
      { property: "og:title", content: "Past Papers — Grade 11 & Grade 12 (CAPS) | adaptED" },
      {
        property: "og:description",
        content:
          "Free CAPS-aligned past papers for Grade 11 and Grade 12. Download official DBE exam papers and turn every paper into personalised revision with adaptED.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://www.adapted.info/past-papers" },
      { property: "og:site_name", content: "adaptED" },
      { name: "twitter:title", content: "Past Papers — Grade 11 & Grade 12 (CAPS) | adaptED" },
      {
        name: "twitter:description",
        content:
          "Free CAPS-aligned past papers for Grade 11 and Grade 12. Download official DBE exam papers and turn every paper into personalised revision with adaptED.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.adapted.info/past-papers" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": "https://www.adapted.info/past-papers#collection",
          url: "https://www.adapted.info/past-papers",
          name: "Past Papers — Grade 11 & Grade 12 (CAPS)",
          description:
            "A curated hub of CAPS-aligned past papers for Grade 11 and Grade 12 South African learners.",
          inLanguage: "en-ZA",
          isPartOf: { "@id": "https://www.adapted.info/#website" },
          hasPart: [
            {
              "@type": "CollectionPage",
              name: "Grade 11 Past Papers (CAPS)",
              url: "https://www.adapted.info/grade-11-past-papers",
            },
            {
              "@type": "CollectionPage",
              name: "Grade 12 Past Papers (CAPS)",
              url: "https://www.adapted.info/grade-12-past-papers",
            },
          ],
        }),
      },
    ],
  }),
  component: PastPapersHub,
});

const grades = [
  {
    to: "/grade-11-past-papers" as const,
    label: "Grade 11",
    title: "Grade 11 past papers & CAPS exam resources",
    description:
      "Build the foundation for matric success. Download Grade 11 exemplar and provincial papers, and turn every question into targeted revision before Grade 12 begins.",
  },
  {
    to: "/grade-12-past-papers" as const,
    label: "Grade 12",
    title: "Grade 12 past papers & CAPS exam resources",
    description:
      "Prepare for your final NSC exams. Download official matric past papers and let adaptED turn every paper into a personalised revision plan.",
  },
];

function PastPapersHub() {
  return (
    <main id="top">
      <PageHero
        label="Exam preparation"
        title="Past papers for Grade 11 & Grade 12"
        paragraph="Free CAPS-aligned past papers and official DBE exam resources for South African learners. Choose your grade to start revising."
      />

      <section className="surface-warm relative py-20">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid gap-8 md:grid-cols-2">
            {grades.map((grade) => (
              <Link
                key={grade.to}
                to={grade.to}
                className="group flex flex-col rounded-2xl border border-foreground/10 bg-background/80 p-8 shadow-[var(--shadow-card)] backdrop-blur-sm transition-transform hover:-translate-y-1"
              >
                <span className="inline-flex w-fit rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-primary">
                  {grade.label}
                </span>
                <h2 className="mt-5 font-display text-2xl leading-tight text-foreground">
                  {grade.title}
                </h2>
                <p className="mt-3 text-base text-foreground/80">{grade.description}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                  View {grade.label} past papers
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}
